import React, { ChangeEvent, FC, FormEvent, Fragment, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Unstable_Grid2 as Grid,
  IconButton,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useFormikContext } from 'formik';

import {
  ContentApproval,
  CreateGroupSourceMutation,
  GroupSourceType,
  SourceStatus,
  UpdateGroupInput,
  useCreateGroupSourceMutation,
} from 'api';
import { useCapabilities, useNotify } from 'hooks';
import { useGroupData } from 'features/groups/hooks';
import { useCreateSource } from 'features/groups/updaters';
import { getGroupHexColor } from 'features/groups/helpers';
import { UrlSchema, UsernameSchema } from 'features/groups/validations';
import { pxToRem } from 'theme';

const styles = {
  form: {
    width: '100%',
  },
  openIcon: {
    fontSize: pxToRem(32),
  },
  addIcon: {
    color: 'common.white',
    borderRadius: 4,
    fontSize: pxToRem(12),
  },
  deleteIcon: {
    transform: 'rotate(45deg)',
    color: 'grey.500',
  },
} as const;

const SourceCreate: FC = () => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { groupColors } = useGroupData();
  const { values } = useFormikContext<UpdateGroupInput>();
  const hexColor = getGroupHexColor(groupColors, values.color);
  const { needs } = useCapabilities();
  const enableTwitterGroupSources = needs('Sharing.Function.EnableTwitterGroupSources');

  const initialState = {
    group_id: values.id,
    source_status: SourceStatus.Disabled,
    source_type: GroupSourceType.Keyword,
    approval: ContentApproval.Submitted,
    source_value: '',
  };

  const [formState, setFormState] = useState(initialState);
  const isAutoApprove = formState.approval === ContentApproval.Autoapproved;
  const { source_value } = formState;
  const mutationOptions = useCreateSource();
  const { mutate } = useCreateGroupSourceMutation({
    ...mutationOptions,
    onSuccess: handleSuccess,
  });

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const sourceValue = event.target.value;
    const isUrl = UrlSchema.isValidSync(sourceValue);
    const isUsername = UsernameSchema.isValidSync(sourceValue);
    let sourceType = GroupSourceType.Keyword;

    if (isUrl) {
      sourceType = GroupSourceType.Rss;
    } else if (isUsername) {
      sourceType = GroupSourceType.TwitterHandle;
    }

    setFormState({
      ...formState,
      source_value: sourceValue,
      source_type: sourceType,
    });
  }

  function handleApproval() {
    setFormState({
      ...formState,
      approval: isAutoApprove ? ContentApproval.Submitted : ContentApproval.Autoapproved,
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    setFormState(initialState);
    mutate(formState);
  }

  function handleSuccess({ clientGroupSourceCreate }: CreateGroupSourceMutation) {
    const message = t('groups:createSourceSuccess', {
      value: clientGroupSourceCreate.source_value,
      group: values.title,
    });

    notify.info({ message });
    setFormState(initialState);
  }

  function renderEndAdornment() {
    if (!source_value) {
      return <AddCircleIcon sx={styles.openIcon} color="primary" style={{ color: hexColor }} />;
    }

    return (
      <Fragment>
        <IconButton onClick={() => setFormState(initialState)} size="large">
          <AddCircleIcon sx={styles.deleteIcon} />
        </IconButton>
        <Button
          variant="contained"
          sx={styles.addIcon}
          type="submit"
          style={{ backgroundColor: hexColor }}
        >
          {t('common:add')}
        </Button>
      </Fragment>
    );
  }

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <TextField
        fullWidth
        value={source_value}
        variant="outlined"
        placeholder={t(
          enableTwitterGroupSources ? 'groups:sourcesInput' : 'groups:sourcesInputRSSKeyword'
        )}
        onChange={handleChange}
        InputProps={{
          endAdornment: renderEndAdornment(),
        }}
        inputProps={{ sx: { padding: 2 } }}
      />
      {source_value && (
        <Grid xs={5}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAutoApprove}
                onChange={handleApproval}
                style={{ color: hexColor }}
              />
            }
            label={t('groups:autoApprove')}
          />
        </Grid>
      )}
    </form>
  );
};

export default SourceCreate;
